import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';

import {
  allSources,
  CareAIChat,
  CareAIChatKnowledgeSource,
  CareAIChatMessage,
} from './care-ai-chat.model';

import { CareAIService } from './care-ai.service';
import { AuthState, selectHasRoles } from '../../core/auth/auth.reducer';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject } from 'rxjs';
import { CareAiPreferencesComponent } from './components/care-ai-preferences/care-ai-preferences.component';

@Component({
  selector: 'carehub-care-ai',
  standalone: true,
  imports: [
    ButtonModule,
    DialogModule,
    InputTextModule,
    FormsModule,
    ConfirmDialogModule,
    CareAiPreferencesComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './care-ai.component.html',
  styleUrl: './care-ai.component.scss',
})
export class CareAIComponent implements OnInit {
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  chats: CareAIChat[] = [];
  activeChat!: CareAIChat;
  prompt: string = '';
  isEditingPreferences: boolean = false;
  readonly isSuperAdmin$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _careAIService: CareAIService,
    private _confirmationService: ConfirmationService,
    private _store: Store<AuthState>
  ) {
    this._store
      .select(selectHasRoles(['SuperAdmin']))
      .pipe(takeUntilDestroyed())
      .subscribe(isSuperAdmin => this.isSuperAdmin$.next(isSuperAdmin ?? false));
  }

  ngOnInit(): void {
    this.chats = this._careAIService.getFromLocalStorage();
  }

  /**
   * Close the CareAI dialog
   */
  close(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }

  /**
   * Open a existing CareAI chat
   *
   * @param chat
   */
  openChat(chat: CareAIChat): void {
    this.activeChat = chat;
  }

  /**
   * Send a prompt to the CareAI
   */
  async sendPrompt(): Promise<void> {
    if (this.prompt.trim() === '') {
      return;
    }
    this.activeChat.promptHistory.push({ userPrompt: this.prompt });
    this.activeChat.lastChatTimestamp = new Date().toISOString();
    this.prompt = '';

    const botResponse: CareAIChatMessage = await this._careAIService.sendChat(this.activeChat);
    this.activeChat.promptHistory[this.activeChat.promptHistory.length - 1].botResponse =
      botResponse.botResponse;

    this._careAIService.saveToLocalStorage(this.chats);
  }

  /**
   * Create a new chat with CareAI
   */
  newPrompt(): void {
    this.chats.push({
      id: this.chats.length + 1,
      promptHistory: [],
      lastChatTimestamp: null,
    });

    this.activeChat = this.chats[this.chats.length - 1];
  }

  /**
   * Delete a chat with CareAI
   */
  deleteChat(aiChat: CareAIChat): void {
    this._confirmationService.confirm({
      message: 'Are you sure that you want to delete the chat? This action cannot be undone.',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-text',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        // Remove the chat from the list
        this.chats = this.chats.filter(chat => chat.id !== aiChat.id);
        // Save the new lists to local storage
        this._careAIService.saveToLocalStorage(this.chats);
        // If the chat being deleted is the active chat, set the active chat to the first chat in the list
        if (this.activeChat.id === aiChat.id) {
          this.activeChat = this.chats[0];
        }
      },
    });
  }

  /**
   * Gets the first user prompt if there is any
   *
   * @param chat CareAIChat
   * @returns The first user prompt or a default message
   */
  getChatTitle(chat: CareAIChat): string {
    return chat.promptHistory[1] ? chat.promptHistory[1].userPrompt : 'New conversation';
  }

  editChatPreferences(): void {
    this.isEditingPreferences = true;
  }

  get sources(): CareAIChatKnowledgeSource[] {
    const isSuperAdmin: boolean = this.isSuperAdmin$.value;

    if (isSuperAdmin) {
      return allSources;
    }

    return allSources.filter(source => !source.superAdminRequired);
  }

  cancelPreferences(): void {
    this.isEditingPreferences = false;
  }

  savePreferences(preferences: CareAIChat['preferences']): void {
    this.activeChat.preferences = preferences;
    this._careAIService.saveToLocalStorage(this.chats);
    this.isEditingPreferences = false;
  }
}
